// signup_data.js
import { ref, onBeforeMount, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useAuthUser } from '../../../utils/stores/modules/authUser/index.js'
import cookieHandler from '../../utils/cookies/cookieHandler.js'
import * as GTM from '../../../utils/analytics/gtm.js'

import { useSignupDataVoucher } from '../../SignupA/composables/signup_data_voucher.js'
import { useSignupDataCheckout } from '../../SignupA/composables/signup_data_checkout.js'

import { usePreparations } from '../../SignupA/composables/preparations.js'
const RESET_SIGNUP_CACHE = process.env.RESET_SIGNUP_CACHE ? process.env.RESET_SIGNUP_CACHE : ''

export function useSignupData() {
	const store = useStore()
  const userStore = useAuthUser()

	const {
		signup_data_voucher,
		handleVoucherInfo,
		clearVoucher,
		updateVoucherData,
		voucher_items,
		delivery_type,
		ignore_card,
	} = useSignupDataVoucher()

	const { signup_data_checkout, updateDeliveryDate, updateCheckoutData } = useSignupDataCheckout()

  const { get_preparation } = usePreparations()

	const signup_data = ref({
		origin: 'signup',
		amount: 0,
    type_name: 'regular',
		coffee_type: 'espresso',
		brewing_method: 'Espresso',
    brewing_method_be: 'wholebean',
		grind_size: 'wholebean',
    recommended_brewing_method_be: 'espresso_machine',
		recommended_grind: 'fine',
		bean_type: 'wholebean',
		is_wholebean: true,
		decaf: false,
		plan: {
			name: undefined,
			price: 0,
      pioneer_price: 0,
			sku: undefined,
      coffee_type: undefined,
      discount_price: undefined,
      discount_pioneer_price: undefined
		},
		frequency: undefined,
		coffee: {
			name: undefined,
			sku: undefined,
			primary_image: undefined,
      card_thumbnail: undefined,
      label_artwork: undefined,
		},
    coffee_card_thumbnail_250g: undefined,
    coffee_card_thumbnail_1kg: undefined,
    coffee_label_artwork: undefined,
		total: 0,
		subtotal: 0,
		discount: 0,
    voucher_discount: 0,
    balance_discount: 0,
		jumper: {
			isLoading: false,
			jumperCode: '',
			isJumper: false,
		},
    is_pioneer: false,
    is_funnel_c: false,
	})

	function loadSignupData(skip_jumper = false) {
		var urlParams = new URLSearchParams(window.location.search)

		if (urlParams.has('jumper') && !skip_jumper) {
			var jumper_code = urlParams.get('jumper')
			store.dispatch('shared/getJumper', { code: jumper_code, callback: handleJumper })
		} else {
			if (urlParams.has('voucher')) {
				var new_voucher_code = urlParams.get('voucher')
				signup_data_voucher.value.voucher_code = new_voucher_code
				store.dispatch('vouchers/getVoucherInfo', {
					code: new_voucher_code,
					callback: handleVoucherInfo,
				})
				cookieHandler.setCookie('voucher-cache', new_voucher_code, '60h')
			} else if (cookieHandler.hasCookie('voucher-cache')) {
				var cached_voucher_code = cookieHandler.getCookie('voucher-cache')
				signup_data_voucher.value.voucher_code = cached_voucher_code
				store.dispatch('vouchers/getVoucherInfo', {
					code: cached_voucher_code,
					callback: handleVoucherInfo,
				})
			}

			if (urlParams.has('fromCoffee')) {
				store.dispatch('shop/getCoffeePlans')
				handleFromCoffee(urlParams)
			} else if (cookieHandler.hasCookie('single-page-signup-2-cache')) {
				var cached_cookie = cookieHandler.getCookie('single-page-signup-2-cache')
        if (
          cached_cookie.cache_creation_time === undefined ||
          cached_cookie.cache_creation_time === null ||
          cached_cookie.cache_creation_time < parseInt(RESET_SIGNUP_CACHE)
        )
          cookieHandler.deleteCookie('single-page-signup-2-cache')
        else
          loadPlanCache(cached_cookie)
			} else {
        const plan_data = {
          type: 'wholebean',
          coffee_type: 'espresso',
          grind: 'wholebean',
          method: undefined,
          range: undefined,
          range_sku: undefined,
          coffee: undefined,
          coffee_sku: undefined,
          amount: undefined,
          coffee_plan_sku: undefined,
        }
        GTM.funnelProgress(3, 'pick_coffee_type', plan_data, 'bag')
        GTM.funnelProgress(4, 'pick_method', { method: 'Espresso' }, 'bag')
        GTM.funnelProgress(5, 'pick_type', { type: 'regular' }, 'bag')
      }
		}
	}

  function setPlanCache(step, forceStep = false) {
    var data = {
      grind_size: signup_data.value.grind_size,
      recommended_grind: signup_data.value.recommended_grind,
      coffee_type: signup_data.value.coffee_type,
      brewing_method: signup_data.value.brewing_method,
      brewing_method_be: signup_data.value.brewing_method_be,
      is_wholebean: signup_data.value.is_wholebean,
      type_name: signup_data.value.type_name,
      bean_type: signup_data.value.bean_type,
      step: step,
      coffee: signup_data.value.coffee,
      plan: signup_data.value.plan,
      decaf: signup_data.value.decaf,
      frequency: signup_data.value.frequency,
      amount: signup_data.value.amount,
      coffee_card_thumbnail_250g: signup_data.value.coffee_card_thumbnail_250g,
      coffee_card_thumbnail_1kg: signup_data.value.coffee_card_thumbnail_1kg,
      coffee_label_artwork: signup_data.value.coffee_label_artwork,
      total: signup_data.value.total,
      discount: signup_data.value.discount,
      cache_creation_time: Date.now()
    }
    if (cookieHandler.hasCookie('single-page-signup-2-cache')) {
      var old_cookie = cookieHandler.getCookie('single-page-signup-2-cache')
      var cookie = {
        ...old_cookie,
        ...data,
        step: step > old_cookie.step || forceStep ? step : old_cookie.step,
      }
      cookieHandler.setCookie('single-page-signup-2-cache', cookie, '5d')
    } else {
      cookieHandler.setCookie('single-page-signup-2-cache', data, '5d')
    }
  }

  function loadPlanCache(plan_cookie) {
    signup_data.value = { ...signup_data.value, ...plan_cookie }
  }

  function handleFromCoffee(urlParams) {
    const coffee_sku = urlParams.get('fromCoffee')
    var coffee = store.getters['shop/getProductByVariantSku'](coffee_sku)
    var grind = urlParams.get('grind')?.replace('-', ' ')
    var coffee_plan = store.getters['shop/getCoffeePlanBySku'](urlParams.get('plan'))
    if (coffee.product_type !== 'pod') {
      var selected_variant = coffee.variants.find((variant) => variant.sku === coffee_sku)
      var thumbnail_250g_variant = coffee.variants.find((variant) => variant.amount_value === 250)
      var thumbnail_1kg_variant = coffee_plan.coffee_type === 'espresso'
        ? coffee.variants.find((variant) => variant.amount_value === 1000)
        : undefined

      var coffee_data = {
        decaf: coffee_plan.decaf,
        amount: coffee_plan.amount,
        coffee_type: coffee_plan.coffee_type,
        grind_size: grind,
        coffee: {
          name: coffee.name,
          sku: coffee_sku,
          primary_image: coffee.primary_image,
          card_thumbnail: selected_variant?.thumbnail_image !== null
            ? selected_variant?.thumbnail_image
            : coffee.product_page.card_thumbnail_image,
          label_artwork: coffee.label_artwork_image,
        },
        plan: {
          name: coffee_plan.plan,
          sku: coffee_plan.sku,
          price: coffee_plan.price,
          pioneer_price: coffee_plan.pioneer_price,
          coffee_type: coffee_plan.coffee_type
        },
        coffee_card_thumbnail_250g: thumbnail_250g_variant?.thumbnail_image !== null
          ? thumbnail_250g_variant?.thumbnail_image
          : coffee.product_page.card_thumbnail_image,
        coffee_card_thumbnail_1kg: thumbnail_1kg_variant?.thumbnail_image,
        coffee_label_artwork: coffee.label_artwork_image,
        bean_type: grind === 'wholebean' ? 'wholebean' : 'ground',
        is_wholebean: grind === 'wholebean',
        type_name: coffee_plan.decaf ? 'decaf' : 'regular',
      }
      if (urlParams.has('prep'))
        coffee_data = {
          ...coffee_data,
          brewing_method: urlParams.get('prep') === 'Stovetop' ?
            'Stove Top' : urlParams.get('prep') === 'Espresso Machines' ?
            'Espresso' : urlParams.get('prep') === 'V60 (Drip)' ?
            'V60' : urlParams.get('prep') === 'Cafetière' ?
            'Cafetiere' :
            urlParams.get('prep')
        }
      else coffee_data = { ...coffee_data, brewing_method: 'V60' }
      if (coffee_data.brewing_method !== 'Wholebean') {
        var brewing_method_be = get_preparation(coffee_data.brewing_method)
        coffee_data['brewing_method_be'] = brewing_method_be.valueBE
      } else {
        coffee_data['brewing_method_be'] = 'wholebean'
      }
      signup_data.value = { ...signup_data.value, ...coffee_data }

      if (urlParams.has('frequency')) {
        signup_data.value = { ...signup_data.value, frequency: urlParams.get('frequency'), amount: coffee_plan.amount }
        setPlanCache(6)
      } else {
        signup_data.value = { ...signup_data.value, amount: 0 }
        setPlanCache(5)
      }
    }
  }

	function handleJumper(result) {
		if (result.status === 'ok') {
			var jumper_package = result.data.signup_package
			if (jumper_package.voucher_code) {
				signup_data_voucher.value.voucher_code = jumper_package.voucher_code
				store.dispatch('vouchers/getVoucherInfo', {
					code: jumper_package.voucher_code,
					callback: handleVoucherInfo,
				})
				cookieHandler.setCookie('voucher-cache', jumper_package.voucher_code, '60h')
			}

      if (jumper_package.coffee_type !== 'pod') {
        if (jumper_package.step >= 7) {
          handleJumperAux(jumper_package)
        } else {
          loadSignupData(true)
        }
      } else {
        loadSignupData(true)
      }
		} else {
			loadSignupData(true)
		}
	}

	function handleJumperAux(jumper_package) {
		signup_data.value = {
			...signup_data.value,
			grind_size: handleDataFromJumper(jumper_package.grind_size),
			coffee_type: jumper_package.coffee_type,
			brewing_method: handleDataFromJumper(jumper_package.brewing_method ? jumper_package.brewing_method : 'V60'),
      brewing_method_be: jumper_package.brewing_method,
      recommended_grind: handleDataFromJumper(jumper_package.grind_size),
			step: 6,
		}
		signup_data.value.jumper.isJumper = true
		signup_data.value.jumper.jumperCode = jumper_package.code

		var coffee = {
			sku: jumper_package.variant_sku,
			name: jumper_package.coffee_display,
			primary_image: jumper_package.coffee_image,
			card_thumbnail: jumper_package.thumbnail_image,
			label_artwork: jumper_package.label_artwork_image,
		}
		var plan = {
			name: jumper_package.coffee_plan.display.split(' ')[0].replace('-','').toLowerCase()+'_plan',
			sku: jumper_package.coffee_plan.sku,
			price: jumper_package.coffee_plan.price,
			pioneer_price: jumper_package.coffee_plan.pioneer_price,
      amount: jumper_package.coffee_plan.amount,
      coffee_type: jumper_package.coffee_type
		}
		signup_data.value = {
			...signup_data.value,
			coffee: coffee,
			frequency: jumper_package.frequency,
			plan: plan,
      amount: jumper_package.coffee_plan.amount,
			coffee_card_thumbnail_250g: jumper_package.thumbnail_image,
			coffee_card_thumbnail_1kg: jumper_package.thumbnail_image,
			coffee_label_artwork: jumper_package.label_artwork_image,
		}
		setPlanCache(6)
	}

	function handleDataFromJumper(str) {
		var splitStr = str.replaceAll('_', ' ').toLowerCase().split(' ')
		for (var i = 0; i < splitStr.length; i++) {
			if (splitStr[i] !== 'to')
				splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
		}
		return splitStr.join(' ')
	}

	function updateSignupData(new_data) {
		signup_data.value = { ...signup_data.value, ...new_data }
	}

	onBeforeMount(() => {
    var get_url_path = window.location.pathname
		if (get_url_path.includes('signup-c'))
      updateSignupData({ is_funnel_c: true })

    if (userStore.isPioneer) {
      var user_is_pioneer = userStore.isPioneer
      updateSignupData({ is_pioneer: user_is_pioneer })
    }
	})

	onMounted(() => {
		loadSignupData()
	})

	return {
		signup_data,
		signup_data_voucher,
		updateSignupData,
		clearVoucher,
		updateVoucherData,
		signup_data_checkout,
		updateCheckoutData,
		updateDeliveryDate,
		voucher_items,
		delivery_type,
		ignore_card,
		setPlanCache,
	}
}
